import React from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { 
  Add, 
  Sports, 
  GroupWork, 
  UploadFile,
  Timeline 
} from '@mui/icons-material';

interface TournamentPlannerProps {
  onCreateTournament: () => void;
}

const GetStarted: React.FC<TournamentPlannerProps> = ({ onCreateTournament }) => {
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      {/* Hero Section */}
      <Paper 
        sx={{ 
          p: 6, 
          textAlign: 'center', 
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Welcome to Wrestling Tournament Manager
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            The complete solution for organizing wrestling tournaments
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.8 }}>
            Create tournaments, manage wrestlers, generate brackets, and track results all in one place
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onCreateTournament}
            startIcon={<Add />}
            sx={{ 
              px: 4, 
              py: 1.5, 
              fontSize: '1.1rem',
              fontWeight: 'bold',
              backgroundColor: 'white',
              color: '#667eea',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              }
            }}
          >
            Create Your First Tournament
          </Button>
        </Box>
      </Paper>

      {/* Features Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
            <Sports sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Tournament Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage multiple wrestling tournaments with ease
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
            <GroupWork sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Wrestler Registration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Import wrestler data via CSV or add manually
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
            <Timeline sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Bracket Generation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Automatic bracket generation with round-robin and elimination formats
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Quick Start Options
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip 
            label="CSV Import Available" 
            color="primary" 
            variant="outlined"
            icon={<UploadFile />}
            sx={{ px: 2, py: 1 }}
          />
          <Chip 
            label="Multiple Bracket Formats" 
            color="secondary" 
            variant="outlined"
            icon={<Timeline />}
            sx={{ px: 2, py: 1 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Ready to organize your next wrestling tournament?
        </Typography>
      </Paper>
    </Box>
  );
};

export default GetStarted;
